import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { CustomIcons } from '@shared/models/custom-icons';
import { PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { IDestroy, TakeUntilDestroy } from '@utils/auto-unsubscribe';
import { EMPTY, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, take, takeUntil, tap } from 'rxjs/operators';
import { MoreVertMenuItem } from '@shared/models/more-vert-menu-item';
import { Clipboard } from '@angular/cdk/clipboard';
import { ActivatedRoute, Router } from '@angular/router';
import { Select, Store } from '@ngxs/store';
import { ActionsExecuting, actionsExecuting } from '@ngxs-labs/actions-executing';
import { cloneDeep } from 'lodash';
import { DOCUMENT, Location } from '@angular/common';
import { convertCatalogItemFieldsVMToView } from '@utils/converters';
import { getAllChildTagsForTd, parseQueryStringByDocument, setLocationQueryParams } from '@utils/other-utils';
import { BreakpointObserver } from '@angular/cdk/layout';

import { CatalogItemFieldsVM, CatalogType, CatalogView } from '@shared/models/shared-catalog.state';
import { FileData, ReleaseStatus, SelectOptionMobile } from '@shared/models/catalog.service';
import { SharedCatalogState } from '@shared/state/catalog/shared-catalog.state';
import { MatDialog } from '@angular/material/dialog';
import { GetRecommendationCatalogItemsForUser } from '@shared/state/shared/shared.action';
import { AuthState } from '@auth/store/auth/auth.state';
import { AuthorizationDialogComponent } from '@auth/components/authorization-dialog/authorization-dialog.component';
import { SnackBarService } from '@shared/components/snackbar-message/services/snackbar.service';
import { MirSnackbarType } from '@shared/components/snackbar-message/model/snackbar';
import { FormControl, FormGroup } from '@angular/forms';
import { CatalogFilter } from '@shared/components/filter-catalog-items/models';
import { MatPaginationCustomComponent } from '@shared/components/mat-pagination-custom/mat-pagination-custom/mat-pagination-custom.component';
import { SearchFilterComponent } from '@shared/components/filter-catalog-items/components/search-filter/search-filter.component';
import { hyphenateSync } from 'hyphen/ru';
import { maxValidator } from '@utils/form-validators';
import { CatalogItemValue } from '../../../../models/catalog';
import { CatalogDynamicColumns, CatalogItemsQueryParams, CatalogStaticColumns } from '../../models/catalog-item-component';
import { DataRouting } from '../../../catalog-data/models/data-routing';
import {
  ChangeCatalogItem,
  ChangedCatalogItemsFilter,
  ChangedCatalogItemsIsNextToMeData,
  ChangedCatalogItemsIsRecommendation,
  ChangedCatalogItemsPaginator,
  DeleteCatalogItem,
  GetCatalogItemsData,
  InitCatalogItemTable,
  SetIsDynamic
} from '../../store/catalog-items.actions';
import { CatalogItemsState } from '../../store/catalog-items-state';
import { CatalogState } from '../../../../store/catalog/catalog.state';
import { CatalogItemCardLinkModalComponent } from '../../../../shared/components/catalog-item-card-info/components/catalog-item-card-link-modal/catalog-item-card-link-modal.component';
import { CatalogItemsModalComponent } from '../../../../shared/components/catalog-item-card-info/components/catalog-items-modal/catalog-items-modal.component';
import { ConnectedPosition } from "@angular/cdk/overlay";
import { CatalogItemsDataService } from './catalog-items-data.service';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'catalog-items',
  templateUrl: './catalog-items.component.html',
  styleUrls: ['./catalog-items.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
@TakeUntilDestroy
export class CatalogItemsComponent implements OnInit, OnDestroy, IDestroy {
  private _currentCatalogId: number;
  private _isNextMeDataNote: boolean | undefined;
  public isMobile = false;
  public icons = CustomIcons;
  public linkType = CatalogType.LINK;
  @ViewChild('staticTable') public staticTable: ElementRef;
  @ViewChild('dynamicTable') public dynamicTable: ElementRef;
  @ViewChild('columnsFilter', { static: true }) public columnsFilter: TemplateRef<any>;
  @ViewChild(SearchFilterComponent) public filterSearch: SearchFilterComponent;
  @ViewChild(MatPaginationCustomComponent, { static: true }) public customPagination: MatPaginationCustomComponent;
  @Select(actionsExecuting([InitCatalogItemTable, GetCatalogItemsData, GetRecommendationCatalogItemsForUser]))
  public loadingInitTable$: Observable<ActionsExecuting>;

  @Select(CatalogItemsState.getFatalError) public isFatalError$: Observable<boolean>;
  @Select(CatalogItemsState.getFileData) public fileData$: Observable<FileData[]>;
  @Select(SharedCatalogState.selectCatalog) public selectCatalog$: Observable<CatalogView>;
  public clickRecommendation$: Observable<any>;
  public form: FormGroup = new FormGroup({
    radius: new FormControl({ value: 2000, disabled: true }, [maxValidator(5000, this.snackBar)]),
    isNextMeData: new FormControl(false)
  });
  public radius = this.form.get('radius');
  public disableSearchNextToMeData = true;
  public allCatalogCount$ = this.store.select(CatalogItemsState.getCatalogItemCount).pipe(map(filterFn => filterFn(this._currentCatalogId)));
  public showTableRightBorder = true;
  public isRecommendationData: boolean;
  public hoverGlobalId: number | null;
  public selectCatalogItemId?: number;
  public componentDestroy: () => Subject<void>;
  public staticColumns: SelectOptionMobile[];
  public staticColumnsInit: SelectOptionMobile[];
  public staticColumnsName: string[];
  public dynamicColumns: SelectOptionMobile[] = [];
  public dynamicColumnsName: string[] = [];
  public dynamicColumnOptions: SelectOptionMobile[] = [];
  public fileColumnNames: string[] = [];
  public menuItems: MoreVertMenuItem[];
  public catalogItemsData$: Observable<CatalogItemValue[] | undefined>;
  public showSelectedItemInfo: boolean;
  public dataSource: MatTableDataSource<CatalogItemValue>;
  public allColumns: CatalogDynamicColumns[];
  public hidePagination = false;
  public overlayPosition: ConnectedPosition[] = [];
  public isDynamic: boolean;
  private isFirstColumnPinned = false;
  public isFirstColumnPinned$ = this.catalogItemsDataService.isFirstColumnPinned.pipe(
    tap(isFirstColumnPinned => {
      this.isFirstColumnPinned = isFirstColumnPinned;
    }),
  );
  showBorder$ = this.isFirstColumnPinned$.pipe(
    map(isPinned => isPinned && this.dynamicColumns.some(x => x.isSelected))
  );
  public tableBorderHeight$ = this.isFirstColumnPinned$.pipe(
    map(() => {
      const table = this.staticTable?.nativeElement?.children[0];
      return (table?.offsetHeight || 0) + 'px';
    }),
  );
  public isNotPublicCatalog = false;
  public dataSourceLoading = true;
  public staticCountRows = 0;
  public breakPointObserve$ = this.breakpointObserver
    .observe([
      '(max-width: 360px)',
      '(max-width: 640px)',
      '(max-width: 800px)',
      '(max-width: 1100px)',
      '(max-width: 1250px)',
      '(max-width: 1400px)',
    ])
    .pipe(
      map(state => {
        this.isMobile = true;
        if (state.breakpoints) {
          if (!state.matches) {
            this.overlayPosition = [
              {
                originX: 'start',
                originY: 'bottom',
                overlayX: 'start',
                overlayY: 'top'
              }
            ];
            return 0;
          }
        }
        this.overlayPosition = [];
        if (!this.catalogItemsDataService.pinChanged.value) {
          this.pinColumn(!state.breakpoints['(max-width: 800px)']);
        }

        if (state.breakpoints['(max-width: 640px)']) {
          return 5;
        }

        if (state.breakpoints['(max-width: 800px)']) {
          return 1;
        }
        this.isMobile = false;
        const selectCatalog = this.store.selectSnapshot(SharedCatalogState.selectCatalog);
        if (state.breakpoints['(max-width: 1100px)'] && selectCatalog.hasGeo) {
          this.overlayPosition = [
            {
              originX: 'start',
              originY: 'bottom',
              overlayX: 'center',
              overlayY: 'top'
            }
          ];
        }

        if (state.breakpoints['(max-width: 1250px)']) {
          return 2;
        }

        if (state.breakpoints['(max-width: 1400px)']) {
          return 3;
        }
        return 4;
      })
    );

  get borderHeight(): string {
    const table = this.staticTable?.nativeElement?.children[0];
    return (table?.offsetHeight || 0) + 'px';
  }

  constructor(
    private clipboard: Clipboard,
    private router: Router,
    private store: Store,
    private activeRoute: ActivatedRoute,
    private location: Location,
    @Inject(DOCUMENT) private document: Document,
    private breakpointObserver: BreakpointObserver,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    private snackBar: SnackBarService,
    private catalogItemsDataService: CatalogItemsDataService,
  ) {}

  public trackRow(index: number): number {
    return index;
  }

  onHover() {
    this.cdr.markForCheck();
  }

  private getMenuItems(): MoreVertMenuItem[] {
    const result: MoreVertMenuItem[] = [
      {
        title: 'DATA.SHARED.MORE_VERT_MENU.COPY_LINK',
        click: element => this.copyLink(element),
        icon: CustomIcons.Links
      }
    ];
    if (this.isDynamic) {
      return result;
    }

    result.push({
      title: 'DATA.SHARED.MORE_VERT_MENU.REPORT_INCORRECT_DATA',
      click: (element): void => {
        this.store.dispatch(new ChangeCatalogItem(cloneDeep(element)));
        this.router.navigate([`/opendata/${DataRouting.Appeals}`], {
          queryParams: { catalogItemGlobalId: element.global_id, catalogGlobalId: this._currentCatalogId }
        });
      },
      icon: CustomIcons.Feedback
    });

    return result;
  }

  private setColumn(res: CatalogItemFieldsVM[]): void {
    if (res.length === 0) {
      return;
    }
    const allColumns = convertCatalogItemFieldsVMToView([...res])
      .sort((a, b) => (a.order > b.order ? 1 : -1))
      .filter(x => x.type !== CatalogType.Catalog);
    let firstStaticColumn = allColumns.find(x => x.systemField?.includes('name'));
    if (!firstStaticColumn) {
      firstStaticColumn = allColumns.find(x => x.systemField?.includes('name') || x.show) ?? allColumns[0];
    }
    const regex = /( |<([^>]+)>)/gi;

    const staticColumns = [
      { name: 'numberClientOpod', title: '№', type: CatalogType.Number, order: -1, catalogId: 1, calculationWidth: 2 },
      { ...firstStaticColumn }
    ];
    this.staticColumns = staticColumns.map(column => ({
      title: column.title,
      isSelected: false,
      name: column.name,
      isNumber: column.type === CatalogType.Number || column.type === CatalogType.Integer || column.type === CatalogType.Numeric,
      isLink: column.type === this.linkType,
      isFile: column.type === CatalogType.File,
      catalogId: column.catalogId,
      calculationWidth: column.title.replace(regex, '').length,
      type: column.type,
      columnContent: this.columnsFilter
    }));
    this.staticColumnsInit = [...this.staticColumns];
    this.staticColumnsName = [...staticColumns.map(x => x.name)];
    this.fileColumnNames = [...allColumns]
      .filter(x => x.type === CatalogType.File)
      .map(x => x.name);

    const dynamicColumns = allColumns.filter(x => x.name !== firstStaticColumn?.name);

    const isAllSelected = this.catalogItemsDataService.isAllSelected.value;
    this.dynamicColumns = this.catalogItemsDataService.selectColumns.value || dynamicColumns.map(column => ({
      title: column.title,
      isSelected: isAllSelected || column.show,
      name: column.name,
      isNumber: column.type === CatalogType.Number || column.type === CatalogType.Integer || column.type === CatalogType.Numeric,
      isLink: column.type === this.linkType,
      isFile: column.type === CatalogType.File,
      catalogId: column.catalogId,
      calculationWidth: column.title.replace(regex, '').length,
    }));
    this.dynamicColumnsName = this.dynamicColumns.map(x => x.name);
    this.dynamicColumnOptions = [...this.dynamicColumns];
    this.allColumns = cloneDeep(allColumns);
    this.cdr.detectChanges();
  }

  private setQueryParams() {
    const newQueryObject: CatalogItemsQueryParams = {
      objectId: undefined,
      filter: undefined,
      pageSize: undefined,
      pageIndex: undefined,
      isRecommendationData: undefined,
      isDynamic: undefined
    };

    const currentCatalogItem = this.store.selectSnapshot(CatalogItemsState.getCatalogItem).call(this, this._currentCatalogId);

    if (currentCatalogItem) {
      this.selectCatalogItemId = currentCatalogItem.selectCatalogItem?.global_id;
      newQueryObject.filter = currentCatalogItem.criteria;
      newQueryObject.objectId = currentCatalogItem.selectCatalogItem?.global_id;
      newQueryObject.pageIndex = currentCatalogItem.pageIndex;
      newQueryObject.pageSize = currentCatalogItem.pageSize;
      if (currentCatalogItem.isDynamic !== undefined) {
        this.isDynamic = currentCatalogItem.isDynamic;
        newQueryObject.isDynamic = currentCatalogItem.isDynamic.toString();
      }
      if (currentCatalogItem.isRecommendationData !== undefined) {
        newQueryObject.isRecommendationData = currentCatalogItem.isRecommendationData.toString();
        this.isRecommendationData = currentCatalogItem.isRecommendationData;
      }
      this.customPagination.setPageIndex(+currentCatalogItem.pageIndex);
      this.customPagination.setPageSize(+currentCatalogItem.pageSize);
    }
    setLocationQueryParams(newQueryObject, this.router, this.location);
  }

  private parseQueryParams() {
    const params = this.activeRoute.snapshot.queryParams as CatalogItemsQueryParams;

    if (params?.isDynamic && params.isDynamic === 'true') {
      this.isDynamic = true;
      this.store.dispatch(new SetIsDynamic(this.isDynamic, false));
    }

    const nextToMeData = this.store.selectSnapshot(CatalogItemsState.getCatalogItemIsNextToMeData).call(this, this._currentCatalogId);
    if (nextToMeData) {
      this.form.get('isNextMeData')?.setValue(nextToMeData.isNextMeData);
      if (nextToMeData.radius) {
        this.form.controls.radius.setValue(+nextToMeData.radius);
      }
      if (this.form.get('isNextMeData')?.value) {
        this.disableSearchNextToMeData = false;
        this.cdr.detectChanges();
        this.form.controls.radius.enable();
      }
    }
    const user = this.store.selectSnapshot(AuthState.authenticatedUser);
    const currentCatalogItem = this.store.selectSnapshot(CatalogItemsState.getCatalogItem).call(this, this._currentCatalogId);
    if (user) {
      if (currentCatalogItem) {
        this.isRecommendationData = currentCatalogItem.isRecommendationData ?? false;
      }
    }

    if (params.objectId || (params.pageIndex && params.pageSize) || params.isRecommendationData) {
      if (params.objectId) {
        this.selectCatalogItemId = +params.objectId;
      }

      if (params.pageIndex && params.pageSize) {
        this.store.dispatch(new ChangedCatalogItemsPaginator(+params.pageIndex, +params.pageSize, false));
        if (currentCatalogItem) {
          this.store.dispatch(new GetCatalogItemsData(this.isDynamic));
        }
        this.customPagination.setPageIndex(+params.pageIndex);
        this.customPagination.setPageSize(+params.pageSize);
      }

      if (user) {
        if (params.isRecommendationData) {
          this.isRecommendationData = params.isRecommendationData.includes('true');
        }
      }
    } else if (this.store.selectSnapshot(CatalogItemsState.getCatalogItem).call(this, this._currentCatalogId)) {
      this.setQueryParams();
    } else if (this.store.selectSnapshot(SharedCatalogState.selectCatalog)) {
      this.isDynamic = this.store.selectSnapshot(SharedCatalogState.selectCatalog).isDynamic;
    }
  }

  private setDefaultPaginatorValue(isLocationChanged = false): void {
    this.store.dispatch(new ChangedCatalogItemsPaginator(0, 10, isLocationChanged));
    this.customPagination.setPageIndex(0);
    this.customPagination.setPageSize(10);
    this.cdr.detectChanges();
  }

  private nextToMeSnackBar(): void {
    this.snackBar.open(
      MirSnackbarType.Info,
      [
        {
          message: 'SNACK_BAR_MESSAGE.ERROR_FILTER_NEXT_TO_ME',
          translate: true
        }
      ],
      5000
    );
  }

  private nextLoading($event: CatalogFilter): void {
    if ($event.nextMeData?.isNextMeData) {
      this.form.get('isNextMeData')?.setValue($event.nextMeData.isNextMeData, {
        emitEvent: false
      });
      this.form.get('radius')?.setValue($event.nextMeData.radius, {
        emitEvent: false
      });
      this.nextMeDataLoading();
      return;
    }
    const nextToMy = this.store.selectSnapshot(CatalogItemsState.getCatalogItemIsNextToMeData).call(this, this._currentCatalogId);
    this.store.dispatch(
      new ChangedCatalogItemsIsNextToMeData({
        ...(nextToMy ?? {}),
        isNextMeData: false
      })
    );

    this.initTable();
  }

  private refusalNextToMeData(): boolean {
    if (this._isNextMeDataNote === false) {
      if (this.form.get('isNextMeData')?.value) {
        this.form.get('isNextMeData')?.setValue(false);
      }

      this.form.controls.radius.disable();
      this.disableSearchNextToMeData = true;
      this.cdr.detectChanges();
      this.nextToMeSnackBar();
      return true;
    }
    return false;
  }

  private getLinkToObject(globalId: string): string {
    const splitHref = this.document.location.href.split('?');
    let objectLink = splitHref[0];
    const selectCatalog = this.store.selectSnapshot(SharedCatalogState.selectCatalog);
    const params = parseQueryStringByDocument(this.document);

    objectLink += `?objectId=${globalId}&isDynamic=${selectCatalog.isDynamic}`;
    if (params?.version) {
      objectLink += `&version=${params.version}`;
    }

    if (params?.release) {
      objectLink += `&release=${params.release}`;
    }

    if (!selectCatalog.isDynamic) {
      objectLink += `&filter=global_id+%3D+${globalId}`;
    } else {
      const catalogInfo = this.store.selectSnapshot(SharedCatalogState.selectCatalogInfo);
      if (catalogInfo) {
        const pkFields = catalogInfo.fields.find(x => x.isPk);
        if (pkFields) {
          objectLink += `&filter=${pkFields.techName}+%3D+${globalId}`;
        }
      }
    }
    return objectLink;
  }

  public copyLink(element: CatalogItemValue): void {
    this.getLinkToObject(element.global_id);

    this.clipboard.copy(this.getLinkToObject(element.global_id));
    this.snackBar.open(MirSnackbarType.Success, [
      {
        translate: true,
        message: 'SNACK_BAR_MESSAGE.LINK_COPY'
      }
    ]);
  }

  public ngOnInit(): void {
    const selectCatalog = this.store.selectSnapshot(SharedCatalogState.selectCatalog);
    const catalogId = selectCatalog?.id;
    const catalogVersion = this.store.selectSnapshot(CatalogState.currentCatalogVersionRelease);
    this.isNotPublicCatalog = catalogVersion?.versionReleaseInfo?.releaseStatus === ReleaseStatus.New;
    if (!catalogId || isNaN(+catalogId)) {
      this.router.navigate(['opendata']);
      return;
    }

    this._currentCatalogId = +catalogId;

    this.parseQueryParams();
    this.initTable();

    this.catalogItemsData$ = this.store.select(CatalogItemsState.getCatalogItemsData).pipe(
      map(filterFn => filterFn(this._currentCatalogId)),
      distinctUntilChanged(),
      tap((data: CatalogItemValue[] | undefined) => {
        if (data?.length) {
          this.staticCountRows = data.length;
        } else {
          this.staticCountRows = 0;
        }

        if (data) {
          this.dataSourceLoading = true;
          if (this.dynamicTable) {
            this.dynamicTable.nativeElement.style.overflow = 'auto';
          }

          if (data.filter(x => x).length === 0) {
            this.dataSource.data = [];
          } else {
            this.dataSource.data = cloneDeep(data).map(x => {
              if (!x) {
                return x;
              }
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              Object.keys(x).forEach(key => {
                if (Array.isArray(x[key]) && x[key].length > 0) {
                  const stringValue = (x[key] as []).join(', ');
                  if (!stringValue.includes('[object Object]')) {
                    x[key] = stringValue;
                  }
                }
              });
              return x;
            });
          }

          if (data.filter(x => x).length === 0) {
            this.hidePagination = true;
          } else {
            this.hidePagination = false;
            this.dataSource.paginator = this.customPagination.paginator;
          }

          if (this.selectCatalogItemId) {
            const currentSelect = data.filter(x => x).find(x => x.global_id === this.selectCatalogItemId);
            if (currentSelect) {
              this.store.dispatch(new ChangeCatalogItem(cloneDeep(currentSelect)));
              if (!this.isMobile) {
                this.showSelectedItemInfo = true;
              }
            }
          }

          // костыль для оптимизации, иначе иногда пытается сразу отрисовать всю таблицу без пагинации
          setTimeout(() => {
            this.addTableRight();
            this.dataSourceLoading = false;
            this.cdr.detectChanges();
          });
        }
      })
    );
    this.menuItems = this.getMenuItems();
    this.store
      .select(CatalogItemsState.getSelectCatalogItem)
      .pipe(
        filter(res => res.call(this, this._currentCatalogId) !== undefined),
        takeUntil(this.componentDestroy())
      )
      .subscribe(res => {
        this.selectCatalogItemId = res.call(this, this._currentCatalogId)?.global_id;
        if (this.selectCatalogItemId && this.customPagination.paginator.length !== 0) {
          this.showSelectedItemInfo = true;
        }
      });

    this.form
      .get('isNextMeData')
      ?.valueChanges.pipe(takeUntil(this.componentDestroy()))
      .subscribe(res => {
        if (this.refusalNextToMeData()) {
          return;
        }
        if (res) {
          this.disableSearchNextToMeData = false;
          this.form.controls.radius.enable();
          this.nextMeDataLoading();
        } else {
          this.form.controls.radius.disable();
          this.disableSearchNextToMeData = true;
          this.store.dispatch(
            new ChangedCatalogItemsIsNextToMeData({
              isNextMeData: false
            })
          );
          this.initTable();
        }
        this.cdr.detectChanges();
      });
  }

  public toolTipText(text: string): string {
    if (!text) {
      return '';
    }

    /*   if (text.length > 200) {
      const newText = `${text.substr(0, 200).trim()}...`;
      return hyphenateSync(newText);
    } */

    if (isNaN(+text) && text.includes(' ')) {
      return hyphenateSync(text);
    }
    return text;
  }

  public addTableRight(): void {
    // this.showBorder = this.isFirstColumnPinned && this.dynamicColumns.some(x => x.isSelected);
    this.cdr.detectChanges();
  }

  public initTable(hasSetDefaultPaginatorValue = false): void {
    if (!this.allColumns) {
      const columns = this.store.selectSnapshot(SharedCatalogState.catalogInfo)?.fields;
      if (columns) {
        this.setColumn(columns);
        this.addTableRight();
      }
    }

    this.dataSource = new MatTableDataSource<CatalogItemValue>([]);
    if (hasSetDefaultPaginatorValue) {
      this.setDefaultPaginatorValue();
    }

    this.cdr.markForCheck();

    this.store
      .dispatch(new InitCatalogItemTable(this.isDynamic))
      .pipe(take(1), takeUntil(this.componentDestroy()))
      .subscribe(() => {
        const count = this.store.selectSnapshot(CatalogItemsState.getCatalogItemsData).call(this, this._currentCatalogId)?.length;
        if (count === 0) {
          this.showSelectedItemInfo = false;
          this.cdr.detectChanges();
        }
        if (count && this.customPagination) {
          this.customPagination.paginator.length = count;
        }
      });
  }

  public paginatorChange($event: PageEvent): void {
    if ($event) {
      this.store.dispatch(new ChangedCatalogItemsPaginator($event.pageIndex, $event.pageSize));
      this.store.dispatch(new GetCatalogItemsData(this.isDynamic));
      if (this.dataSource) {
        this.dataSource.paginator = this.customPagination.paginator;
        if (!this.dataSource.data[($event.pageIndex + 1) * ($event.pageSize - 1)]) {
          this.dynamicTable.nativeElement.style.overflow = 'hidden';
        }
      }
    }
  }

  public setSelectedItem(target: EventTarget | null, currentRow: CatalogItemValue): void {
    if (!currentRow) {
      return;
    }
    const topTags = ['APP-HOVERABLE-SVG', 'svg'];
    const parentTags = getAllChildTagsForTd(topTags, target as HTMLElement);
    if (!parentTags.some(parentTag => topTags.some(topTag => topTag === parentTag))) {
      this.store.dispatch([
        new ChangeCatalogItem(cloneDeep(currentRow)),
        new ChangedCatalogItemsPaginator(this.customPagination.paginator.pageIndex, this.customPagination.paginator.pageSize)
      ]);

      this.addTableRight();
    }
  }

  public changeRowHover($event: MouseEvent, globalId: number): void {
    if ($event.type === 'mouseenter') {
      this.hoverGlobalId = globalId;
      return;
    }
    this.hoverGlobalId = null;
  }

  public changeFilter($event: CatalogFilter, isFilterClear = false): void {
    this.customPagination.setPageIndex(0);
    this.store.dispatch(new ChangedCatalogItemsPaginator(0, this.customPagination.paginator.pageSize));
    this.store.dispatch(new ChangedCatalogItemsFilter($event.criteria));
    if (isFilterClear) {
      this.isRecommendationData = false;
      this.form.get('isNextMeData')?.setValue(false, {
        emitEvent: false
      });
      this.form.get('radius')?.setValue('2000', {
        emitEvent: false
      });
      this.form.get('radius')?.disable({
        emitEvent: false
      });
      this.store.dispatch(
        new ChangedCatalogItemsIsNextToMeData({
          radius: 2000,
          isNextMeData: false
        })
      );
    }

    if ($event.isRecommendationData) {
      this.isRecommendationData = $event.isRecommendationData;
      this.getRecommendation($event.isRecommendationData)
        .pipe(take(1), takeUntil(this.componentDestroy()))
        .subscribe(res => {
          if (typeof res !== 'boolean') {
            this.nextLoading($event);
          }
        });
      return;
    }
    this.store.dispatch(new ChangedCatalogItemsIsRecommendation(false));

    this.nextLoading($event);
  }

  public reloadPage(): void {
    this.document?.defaultView?.location.reload();
  }

  public ngOnDestroy(): void {
    if (this.isDynamic) {
      this.store.dispatch(new DeleteCatalogItem(this._currentCatalogId));
    }
    this.catalogItemsDataService.selectColumns.next(this.dynamicColumns);
    this.catalogItemsDataService.isFirstColumnPinned.next(this.isFirstColumnPinned);
    this.catalogItemsDataService.pinChanged.next(false);
  }

  public getRecommendationClick(isRecommendation: boolean, hasInitTable = true): void {
    this.dataSource = new MatTableDataSource<CatalogItemValue>([]);
    this.setDefaultPaginatorValue(true);
    this.clickRecommendation$ = this.getRecommendation(isRecommendation).pipe(
      tap(res => {
        if (typeof res === 'boolean') {
          if (!res) {
            this.isRecommendationData = false;
            this.cdr.detectChanges();
          }
        } else {
          this.store.dispatch(new ChangedCatalogItemsIsRecommendation(true));
          if (hasInitTable) {
            this.initTable();
          }
        }
      })
    );
  }

  public getRecommendation(isRecommendation: boolean, hasInitTable = true): Observable<any> {
    const user = this.store.selectSnapshot(AuthState.authenticatedUser);
    if (!user) {
      const authorizationDialog = this.dialog.open(AuthorizationDialogComponent, { disableClose: true });
      authorizationDialog.componentInstance.keyOperations = 'GetRecommendation';
      authorizationDialog.componentInstance.operationValue = this._currentCatalogId;
      return authorizationDialog.afterClosed().pipe(take(1), takeUntil(this.componentDestroy()));
    }
    if (!this.isRecommendationData) {
      this.store.dispatch(new ChangedCatalogItemsIsRecommendation(true));
      return this.store.dispatch(new GetRecommendationCatalogItemsForUser(this._currentCatalogId)).pipe(takeUntil(this.componentDestroy()));
    }
    this.store.dispatch(new ChangedCatalogItemsIsRecommendation(isRecommendation));
    if (hasInitTable) {
      this.initTable();
    }
    return EMPTY;
  }

  public nextMeDataLoading(hasInitTable = true): void {
    this.setDefaultPaginatorValue(true);
    const radius = this.form.get('radius');
    if (radius?.value > 5000) {
      radius?.setValue(5000);
    } else if (radius?.value < 100) {
      radius?.setValue(100);
    }

    const window = this.document.defaultView;

    if (this.refusalNextToMeData()) {
      return;
    }

    if (this._isNextMeDataNote === undefined) {
      this.nextToMeSnackBar();
    }

    if (window) {
      window.navigator.geolocation.getCurrentPosition(
        res => {
          this.store.dispatch(
            new ChangedCatalogItemsIsNextToMeData({
              isNextMeData: this.form.get('isNextMeData')?.value,
              latitude: res.coords.latitude,
              longitude: res.coords.longitude,
              radius: this.form.get('radius')?.value
            })
          );
          if (hasInitTable) {
            this.initTable();
          }
          this._isNextMeDataNote = true;
        },
        () => {
          this._isNextMeDataNote = false;
          this.refusalNextToMeData();
        }
      );
    }
  }

  public dynamicScrollChange($event: Event): void {
    if (this.staticTable && this.staticTable.nativeElement) {
      this.staticTable.nativeElement.scrollTop = ($event.target as Element).scrollTop;
    }
  }

  public staticScrollChange($event: Event): void {
    if (this.dynamicTable && this.dynamicTable.nativeElement) {
      this.dynamicTable.nativeElement.scrollTop = ($event.target as Element).scrollTop;
    }
  }

  public pinColumn(pinned: boolean) {
    this.catalogItemsDataService.isFirstColumnPinned.next(pinned);
    this.catalogItemsDataService.pinChanged.next(true);
    if (pinned) {
      this.staticColumns = [...this.staticColumnsInit];
      this.dynamicColumns = this.dynamicColumns.filter(col => !this.staticColumnsName.includes(col.name));
      this.dynamicColumnOptions = [...this.dynamicColumns];
      this.dynamicColumnsName = this.dynamicColumns.map(c => c.name);
    } else {
      const staticColumns = [...(this.staticColumns || [])];
      const dynamicColumns = [
        ...staticColumns.map(col => ({ ...col, isSelected: true })),
        ...this.dynamicColumns,
      ];
      const dynamicColumnsName = [...new Set(dynamicColumns.map(x => x.name))];
      this.dynamicColumns = dynamicColumnsName.map(name => dynamicColumns.find(col => col.name === name)!);
      this.dynamicColumnOptions = this.dynamicColumns.filter(col => !staticColumns.map(c => c.name).includes(col.name));
      this.dynamicColumnsName = dynamicColumnsName;
      this.staticColumns = [];
    }
  }

  public clearFilter(): void {
    this.filterSearch.cancelFilter(false);
    this.changeFilter(
      {
        isMobile: this.isMobile,
        criteria: '',
        isRecommendationData: false,
        nextMeData: {
          radius: 2000,
          isNextMeData: false
        }
      },
      true
    );
  }

  public openLinkCard(value: { global_id: number; value: string }, column: SelectOptionMobile): void {
    const modalDialog = this.dialog.open(CatalogItemCardLinkModalComponent, { backdropClass: 'closer-backdrop-transparent' });
    modalDialog.componentInstance.globalId = +value.global_id;
    modalDialog.componentInstance.title = column.title;
    modalDialog.componentInstance.titleInner = value.value;
    modalDialog.componentInstance.catalogId = +column.catalogId;
  }

  public openArrayCard(value: { global_id: number; value: string }[], column: SelectOptionMobile | CatalogStaticColumns): void {
    const modalDialog = this.dialog.open(CatalogItemsModalComponent, {
      backdropClass: 'closer-backdrop-transparent',
      width: '100%',
      maxWidth: '710px'
    });
    modalDialog.componentInstance.catalogId = +column.catalogId;
    modalDialog.componentInstance.title = column.title;
    modalDialog.componentInstance.arr = value;
  }

  public isFileColumn(element: CatalogItemValue, columnName: string): boolean {
    return this.fileColumnNames.includes(columnName) && !!element[columnName];
  }

  public getFileData(element: CatalogItemValue, columnName: string, allFileData: FileData[]): FileData | undefined {
    const fieldId = element[columnName];
    return allFileData.find(file => file.id === fieldId);
  }

  public isFirstDynamicColumn(column: SelectOptionMobile): boolean {
    const firstSelected = this.dynamicColumns.find(x => x.isSelected);
    return firstSelected?.name === column.name;
  }

  maxWidth(breakPointScreen: number) {
    return breakPointScreen === 5
      ? '30%'
      : breakPointScreen === 5 ? '50%' : '100%';
  }
}
